import { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw, Clock } from 'lucide-react';
import { syncManager } from '../../lib/syncManager';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      setIsOnline(navigator.onLine);
      updatePendingCount();
      updateLastSync();
    };

    const updatePendingCount = async () => {
      const count = await syncManager.getPendingSyncCount();
      setPendingCount(count);
    };

    const updateLastSync = async () => {
      const time = await syncManager.getLastSyncTime();
      setLastSync(time);
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    // Update counts periodically
    const interval = setInterval(() => {
      updatePendingCount();
      updateLastSync();
    }, 5000);

    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await syncManager.syncPendingChanges();
      setPendingCount(await syncManager.getPendingSyncCount());
      setLastSync(await syncManager.getLastSyncTime());
    } finally {
      setSyncing(false);
    }
  };

  const formatLastSync = (timestamp: number | null): string => {
    if (!timestamp) return 'Never';
    
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (isOnline && pendingCount === 0) {
    return null; // Don't show indicator when online and synced
  }

  return (
    <div className="fixed bottom-4 left-4 z-[9998]">
      <div className="bg-card border border-border rounded-lg shadow-lg p-3 max-w-xs">
        <div className="flex items-center gap-3">
          {isOnline ? (
            <Wifi className="w-5 h-5 text-green-500 flex-shrink-0" />
          ) : (
            <WifiOff className="w-5 h-5 text-orange-500 flex-shrink-0 animate-pulse" />
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {isOnline ? 'Online' : 'Offline Mode'}
              </span>
              {pendingCount > 0 && (
                <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-500 rounded text-xs font-bold">
                  {pendingCount}
                </span>
              )}
            </div>
            
            {pendingCount > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {pendingCount} change{pendingCount !== 1 ? 's' : ''} pending sync
              </p>
            )}
            
            {lastSync && (
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>Last sync: {formatLastSync(lastSync)}</span>
              </div>
            )}
          </div>

          {isOnline && pendingCount > 0 && (
            <button
              onClick={handleManualSync}
              disabled={syncing}
              className="p-1.5 hover:bg-accent rounded transition-colors disabled:opacity-50"
              title="Sync now"
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${syncing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
